import { zodResolver } from '@hookform/resolvers/zod';
import { graphql, useStaticQuery } from 'gatsby';
import { always, cond, equals, find, map } from 'ramda';
import React, { FC, useContext, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useAsync, useDeepCompareEffect } from 'react-use';
import styled from 'styled-components';
import { z } from 'zod';

import BasicText, { IBasicTextProps } from '@ui/atoms/BasicText';
import Button from '@ui/atoms/Button';
import FlexColumn from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import SelectableButton from '@ui/atoms/SelectableButton';
import Skeleton from '@ui/atoms/Skeleton';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';
import NewInputWrapper from '@ui/molecules/NewInputWrapper';

import { medicationDeliveryConstants } from '@ui/routes/shared/MedicationDeliveryStep';

import spaced from '@ui/styled/Spaced';

import useMemberInfo from '@hooks/useMemberInfo';

import { getServiceType } from '@helpers/MentalHealth/mentalHealth';

import { commonErrors } from '@utils/error';

import { isBrowser } from '@providers/AuthProvider';
import FlowContext from '@providers/FlowContextProvider';

import unfilteredTopics, { MHTopic, MHTopicId } from '@data/mh_topics';
import { Topic } from '@data/topics';

import { IFlowStepProps } from '@folx-types/flow-steps';
import { SpacingTypes } from '@folx-types/theme';

import theme from '@theme/FolxTheme';

import { mentalHealthTopicConstants } from './MemberMHTopicSelectionStep.data';

interface ITopicSelectionStepProps extends IFlowStepProps {}

interface ITopicSelectionStepContent {
	pageContent: {
		title: string;
		topic_selection_title: string;
		specify_title: string;
		specify_error: string;
		next_button_title: string;
	};
}

const SpacedText = spaced<IBasicTextProps>(BasicText);

const SpacedGridItem = styled(CenteredGridItem)<{ marginBottom: SpacingTypes }>`
	margin-bottom: ${({ marginBottom }) => theme.spacing[marginBottom]};
`;

const SpacedInputWrapper = styled(NewInputWrapper)<{
	marginBottom: SpacingTypes;
}>`
	margin-bottom: ${({ marginBottom }) => theme.spacing[marginBottom]};
`;

const TopicContainer = styled(FlexColumn)`
	justify: center;
	margin-bottom: 2.4rem;
	gap: ${theme.spacing.sm};

	${SelectableButton} {
		width: 100%;
	}
`;

const NavigationWrapper = styled.div`
	align-items: center;
	display: flex;
	justify-content: flex-end;
	gap: ${theme.spacing.lg};
`;

const MHTopicSelectionStep: FC<ITopicSelectionStepProps> = ({ stepKey }) => {
	const { memberInfo } = useMemberInfo();
	const [loading, setLoading] = useState<boolean>(true);

	const { pageContent } = useStaticQuery<ITopicSelectionStepContent>(
		graphql`
			{
				pageContent: butterPage(slug: { eq: "mental-health-topic-selection" }) {
					title
					topic_selection_title
					specify_title
					specify_error
					next_button_title
				}
			}
		`
	);
	const { title, topic_selection_title, next_button_title } = pageContent;

	const context = useContext(FlowContext);
	const { handleFormSubmission, existingFormData } = context;

	const schema = z.object({
		topic: z.string(),
	});
	type Schema = Partial<z.input<typeof schema>>;

	const { control, handleSubmit, register, formState } = useForm<Schema>({
		defaultValues: existingFormData,
		resolver: zodResolver(schema),
		mode: 'onChange',
	});
	const { errors } = formState;

	const { value: topics } =
		useAsync(async () => {
			return unfilteredTopics.filter((topic) => {
				console.log('topic.id', topic.id);

				return cond([
					[equals(MHTopic.TALK_THERAPY), always(memberInfo?.stateOfResidence?.mentalHealthTherapy)],
					[
						equals(MHTopic.MENTAL_HEALTH_LETTER),
						always(memberInfo?.stateOfResidence?.mentalHealthTherapy),
					],
					[
						equals(MHTopic.MEDICATION_MANAGEMENT),
						always(memberInfo?.stateOfResidence?.mentalHealthMedication),
					],
					[equals(MHTopic.ADHD), always(memberInfo?.stateOfResidence?.adhd)],
				])(topic.id as MHTopic);
			});
		}, [memberInfo]) ?? [];

	const onSubmit = async (data: Record<string, any>) => {
		const topic: Topic | Record<string, unknown> =
			find((t) => t.name === data.topic, topics ?? []) ?? {};

		data[mentalHealthTopicConstants.TOPIC_ID] = topic?.id ?? '';
		const serviceType = getServiceType(data[mentalHealthTopicConstants.TOPIC_ID], { vcFlow: true });

		data[medicationDeliveryConstants.MEDICATION_DELIVERY_METHOD] =
			serviceType === MHTopicId.vc ? medicationDeliveryConstants.PHARMACY_METHOD : null;

		// we need to clear out appointment data if they've gone back and chosen a different service type
		// todo: best-effort cancelation of held slot
		console.log(
			`cogsworthAppointmentServiceType: ${existingFormData.cogsworthAppointmentServiceType} `
		);
		console.log(`data.topicID: ${existingFormData?.[mentalHealthTopicConstants.TOPIC_ID]} `);
		if (
			existingFormData.cogsworthAppointmentServiceType &&
			existingFormData.cogsworthAppointmentServiceType !==
				getServiceType(data[mentalHealthTopicConstants.TOPIC_ID])
		) {
			data.cogsworthId = null;
			data.cogsworthAppointmentTime = null;
			data.cogsworthAppointmentRescheduleLink = null;
			data.cogsworthAppointmentCancelLink = null;
			data.cogsworthAppointmentServiceType = null;
		}

		await handleFormSubmission(data, stepKey);
	};

	useDeepCompareEffect(() => {
		if (!topics || topics.length === 0) {
			return;
		}

		const topicId = isBrowser && window.localStorage.getItem('mh_topic');

		if (topicId) {
			const topic = unfilteredTopics.find((topic) => topic.id === topicId);
			onSubmit({ topic: topic?.name });
		} else {
			setLoading(false);
		}

		isBrowser && window.localStorage.removeItem('mh_topic');
	}, [topics]);

	if (loading) {
		return (
			<>
				<SpacedGridItem marginBottom="lgxl">
					<Skeleton fullWidth height="lg" />
				</SpacedGridItem>
				<SpacedGridItem marginBottom="lgxl">
					<SpacedGridItem marginBottom="md">
						<Skeleton fullWidth height="md" />
					</SpacedGridItem>
					<SpacedGridItem marginBottom="md">
						<Skeleton structure="column" gap="sm" repeated={3} fullWidth height="xxxl" />
					</SpacedGridItem>
					<SpacedGridItem marginBottom="md">
						<Skeleton fullWidth height="lgxl" />
					</SpacedGridItem>
				</SpacedGridItem>
			</>
		);
	}

	return (
		<>
			<SpacedGridItem marginBottom="lgxl">
				<HeadingText>{title}</HeadingText>
			</SpacedGridItem>
			<SpacedGridItem marginBottom="lgxl">
				<form onSubmit={handleSubmit(onSubmit)}>
					<SpacedInputWrapper
						title={topic_selection_title}
						marginBottom="lg"
						error={!!errors?.topic && commonErrors.selectionRequired}>
						<Controller
							name="topic"
							control={control}
							render={(args) => {
								const field = {
									...args.field,
									value: args.field.value ?? '',
								};
								return (
									<TopicContainer marginBottom="lgxl">
										{map((topic: Topic) => {
											const topicName = topic.name;

											return (
												<SelectableButton key={topicName} optionValue={topicName} {...field}>
													{topicName}
												</SelectableButton>
											);
										})(topics ?? [])}
									</TopicContainer>
								);
							}}
						/>
					</SpacedInputWrapper>
					<NavigationWrapper>
						<Button
							variant="secondary"
							label={next_button_title}
							disabled={false}
							labelSize="md"
							type="submit"
						/>
					</NavigationWrapper>
				</form>
			</SpacedGridItem>
		</>
	);
};

export default MHTopicSelectionStep;
